import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { CircleX } from 'lucide-react'
import { emit } from '../analytics/analytics'
import { Icon } from '../icons/Icon'
import { Overlay } from '../overlay/Overlay'
import { Button } from './Button'
import './dialog.css'

/**
 * The two dialogs — M0 0.12, #194.
 *
 * Both wrap `Overlay`, which owns the portal, the scrim, the focus trap,
 * Escape, the focus restore and the inert background. Nothing here re-implements
 * any of that; the difference between the two is what sits inside the panel and
 * what a control does when it is pressed.
 *
 * **`role="alertdialog"` on both, not `dialog`.** Both interrupt: one reports
 * something that already happened, the other guards something about to. The
 * role is what tells assistive technology to announce the body on open rather
 * than waiting to be navigated into.
 *
 * **The surface is the overlay's.** `.overlay__panel` is an opaque `--surface`,
 * which is what makes a destructive control's translucent `--neg-soft` fill
 * measurable — see `dialog.css`. The plan asked for a `<Panel>` wrapper for that
 * reason; #214 shipped the fill on the shell itself, so the wrapper would now
 * paint and pad the same surface twice. `Dialog.test.tsx` asserts the property
 * rather than the mechanism.
 */

type Shared = {
  open: boolean
  title: string
  /** Required, and `ReactNode` so a caller can emphasise the thing being lost. */
  body: ReactNode
  /**
   * Where focus goes when the control that opened this is gone by the time it
   * closes — a Remove button whose success path unmounts it, most often.
   * Forwarded to `Overlay`; `.focus()` on a detached node is a silent no-op.
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
}

/**
 * Something already happened and the user has to be told. One control, and it
 * only dismisses.
 */
export function AlertDialog({
  open,
  onClose,
  title,
  body,
  acknowledge = 'Close',
  restoreFocusTo,
}: Shared & { onClose: () => void; acknowledge?: string }) {
  return (
    <Overlay
      open={open}
      onClose={onClose}
      label={title}
      role="alertdialog"
      restoreFocusTo={restoreFocusTo}
    >
      <div className="dialog">
        {/* `h2`, not `h1`: `ChromeLayout` owns the document's only `h1`, and a
            dialog is a section of the page it interrupts rather than a new
            document. The accessible NAME comes from `Overlay`'s `aria-label`,
            which is why this carries no id wiring. */}
        <h2 className="dialog__title">{title}</h2>
        <p className="dialog__body">{body}</p>
        <div className="dialog__actions">
          {/* `outline`, not `primary`. `--accent` may fill exactly one element
              inside the page body, and an acknowledgement is not it. */}
          <Button variant="outline" size="page" onClick={onClose}>
            {acknowledge}
          </Button>
        </div>
      </div>
    </Overlay>
  )
}

/**
 * Three states, as a union rather than two booleans: `pending && failed` is
 * unreachable and should be unrepresentable.
 */
type Status =
  | { kind: 'idle' }
  | { kind: 'pending'; via: 'confirm' | 'alternative' }
  | { kind: 'failed'; reason: string }

const IDLE: Status = { kind: 'idle' }

/**
 * The last thing standing between a user and something irreversible.
 *
 * **`onConfirm` returns a promise, and that is the whole design.** #194's last
 * criterion — *"a confirmation whose action can fail must show the failure
 * without closing"* — is unimplementable with `() => void`, because the dialog
 * would have no way to learn the outcome. It awaits; on rejection it stays open,
 * renders the reason and re-enables its controls.
 *
 * **The caller closes it on success.** `open` is controlled, so this component
 * cannot close itself, and the work that succeeded is the caller's — in
 * `Security.tsx` the same commit refreshes the list and raises the toast. A
 * `onSuccess`-shaped fourth callback would only be the caller's own `finally`
 * spelled twice.
 *
 * **`errorFor` is optional in the type and not in spirit.** A rejection is
 * `unknown`; stringifying it prints "Error: auth/requires-recent-login", and a
 * generic fallback prints something close to "Something went wrong" — which
 * CLAUDE.md names as a defect wherever something specific is possible. The
 * fallback below is therefore the most specific thing that is true without the
 * prop: that the action did not happen and nothing changed. Callers with a
 * translation — `Security.tsx` has one — must pass it.
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  errorFor,
  mode,
  title,
  body,
  confirmLabel,
  pendingLabel = 'Working…',
  cancelLabel = 'Cancel',
  destructive = false,
  alternative,
  restoreFocusTo,
}: Shared & {
  onCancel: () => void
  /**
   * Resolve `'handed_over'` when the work moved to another confirmation (a
   * re-authentication prompt) rather than being done: that dialog will emit its
   * own `confirmed` or `cancelled`, and counting one here too counts it twice.
   */
  onConfirm: () => Promise<void | 'handed_over'>
  errorFor?: (thrown: unknown) => string
  /**
   * The action enum for analytics — `remove_factor`, `hide_account`.
   *
   * A required prop, and the plan's interface omitted it while separately
   * requiring the event: #194 asks that "analytics on confirm/cancel carries the
   * action enum", and there is nowhere else for the enum to come from. It is
   * never the title or the body, which name a specific account or factor.
   */
  mode: string
  confirmLabel: string
  /** What the confirming control says while the promise is unsettled. */
  pendingLabel?: string
  /** The dismissing control's wording — "Keep it" where Cancel is ambiguous. */
  cancelLabel?: string
  destructive?: boolean
  /**
   * A third, non-destructive way out — "Pause instead" beside "Delete rule".
   * It sits between Cancel and the confirm, shares the pending state (every
   * control is disabled while EITHER promise is unsettled), and reports a
   * rejection through `errorFor` like the confirm. The caller closes on
   * success. `pendingLabel` is what it says while it is the one running.
   */
  alternative?: { label: string; onAct: () => Promise<void>; pendingLabel?: string }
}) {
  const [status, setStatus] = useState<Status>(IDLE)
  const stack = useRef<HTMLDivElement>(null)

  // A dialog reopened after a failure must not still be showing the last one.
  // Derived state is not available here — the reset is a transition, not a
  // render-time function of `open` — but this is the narrow, honest use of an
  // effect: one piece of state reset by one external change.
  useEffect(() => {
    if (!open) setStatus(IDLE)
  }, [open])

  const busy = status.kind === 'pending'

  /**
   * Park focus on the panel's content while every control in it is disabled.
   *
   * **Without this the user is thrown out of the modal**, and it is invisible to
   * this suite. Pressing the confirming button focuses it; the same commit
   * disables it; a BROWSER then blurs a disabled element to `<body>`, which is
   * outside the trap and outside `Overlay`'s Escape handler — that handler is on
   * the panel, deliberately, so that two stacked overlays close one at a time.
   * So an in-flight dialog left the keyboard at the top of a background document
   * it cannot see, with no way back in and no way out.
   *
   * jsdom does neither — it keeps focus on the disabled button — which is why
   * measuring "is focus still inside the panel" would have been green over the
   * whole defect. `Overlay`'s own docstring records the same asymmetry, and
   * `useFocusTrap` has an empty-trap branch for exactly this case that never
   * runs here: it is keyed on `open`, and `open` does not change when the
   * controls do.
   *
   * The assertion that has teeth is that focus is not on something DISABLED,
   * which is true in both engines and false without this effect.
   */
  useEffect(() => {
    if (busy) stack.current?.focus()
  }, [busy])

  function cancel() {
    // Not while the request is in flight. It has already been sent, so closing
    // now throws away the surface its failure would land on and tells the user
    // they cancelled something they did not.
    if (busy) return
    emit('confirmation_result', { mode, result_status: 'cancelled' })
    onCancel()
  }

  async function run(via: 'confirm' | 'alternative', action: () => Promise<void | 'handed_over'>) {
    if (busy) return
    // Only the alternative names itself, as `variant` (`choice` is not in the vocabulary): the confirm's event is unchanged.
    const extra = via === 'alternative' ? { variant: 'alternative' } : {}
    setStatus({ kind: 'pending', via })
    try {
      const outcome = await action()
      emit('confirmation_result', {
        mode,
        result_status: outcome === 'handed_over' ? 'handed_over' : 'confirmed',
        ...extra,
      })
      // Deliberately not resetting to idle: the caller closes on success, and
      // the effect above resets when it does. Flipping back here would repaint
      // the live controls for one frame under a dialog on its way out.
    } catch (thrown) {
      emit('confirmation_result', { mode, result_status: 'failed', ...extra })
      setStatus({
        kind: 'failed',
        reason:
          errorFor?.(thrown) ??
          'That did not work, and nothing has changed. Try again in a moment.',
      })
    }
  }

  return (
    <Overlay
      open={open}
      onClose={cancel}
      label={title}
      role="alertdialog"
      restoreFocusTo={restoreFocusTo}
    >
      {/* `tabIndex={-1}` makes this focusable without adding a Tab stop —
          `useFocusTrap` excludes `[tabindex="-1"]` from its wrap for that very
          reason, so parking focus here does not put it in the cycle. */}
      <div className="dialog" ref={stack} tabIndex={-1}>
        <h2 className="dialog__title">{title}</h2>
        <p className="dialog__body">{body}</p>
        {status.kind === 'failed' && (
          // Its own live region, because this arrives AFTER the dialog opened.
          // `alertdialog` announces its contents once, on open; a message added
          // later is announced by nothing unless it says so itself.
          <p className="dialog__error" role="alert">
            <span className="dialog__error-mark">
              <Icon glyph={CircleX} />
            </span>
            {status.reason}
          </p>
        )}
        {/* Cancel FIRST in the DOM. `useFocusTrap` focuses the first focusable
            in the panel, so this is what opens a destructive dialog on the safe
            control rather than on the one that destroys something. `dialog.css`
            paints the row reversed, so the confirming action still sits at the
            trailing edge. */}
        <div className="dialog__actions">
          <Button variant="outline" size="page" disabled={busy} onClick={cancel}>
            {cancelLabel}
          </Button>
          {alternative !== undefined && (
            <Button
              variant="outline"
              size="page"
              disabled={busy}
              aria-busy={busy && status.via === 'alternative'}
              onClick={() => void run('alternative', alternative.onAct)}
            >
              {status.kind === 'pending' && status.via === 'alternative'
                ? (alternative.pendingLabel ?? 'Working…')
                : alternative.label}
            </Button>
          )}
          <Button
            // `destructive` is `--neg-soft` + `--neg`, the same pairing
            // `AlertPanel` uses rather than a third one. A non-destructive
            // confirm is `primary` — it is the page body's one lime fill while
            // it is up, and the sole reason the user is looking at this panel.
            variant={destructive ? 'destructive' : 'primary'}
            size="page"
            disabled={busy}
            // Announces the wait to a screen reader, which the label change
            // alone does not — the button is disabled, so focus has left it.
            aria-busy={status.kind === 'pending' && status.via === 'confirm'}
            onClick={() => void run('confirm', onConfirm)}
          >
            {status.kind === 'pending' && status.via === 'confirm' ? pendingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </Overlay>
  )
}
